// CEL syntax highlighting for CodeMirror: a small tokenizer (strings with
// r/b prefixes and triple quotes, numbers with u and exponents, keywords,
// function calls, operators, // comments).
import { HighlightStyle, StreamLanguage, syntaxHighlighting } from '@codemirror/language'
import { tags as t } from '@lezer/highlight'

interface State {
  quote: string | null // open string delimiter
  raw: boolean
}

function readString(stream: { eol(): boolean; next(): string | void; match(s: string): boolean | RegExpMatchArray | null }, state: State): string {
  while (!stream.eol()) {
    if (state.quote && stream.match(state.quote)) {
      state.quote = null
      return 'string'
    }
    const ch = stream.next()
    if (ch === '\\' && !state.raw) stream.next()
  }
  if (state.quote && state.quote.length === 1) state.quote = null // single-line strings end at the line
  return 'string'
}

export const celLanguage = StreamLanguage.define<State>({
  name: 'cel',
  startState: () => ({ quote: null, raw: false }),
  token(stream, state) {
    if (state.quote) return readString(stream, state)
    if (stream.eatSpace()) return null
    if (stream.match('//')) {
      stream.skipToEnd()
      return 'comment'
    }
    const s = stream.match(/^([rR][bB]?|[bB][rR]?|[rRbB])?("""|'''|"|')/) as RegExpMatchArray | null
    if (s) {
      state.quote = s[2]
      state.raw = /r/i.test(s[1] ?? '')
      return readString(stream, state)
    }
    if (stream.match(/^(0x[0-9a-fA-F]+u?|\d+\.\d*([eE][+-]?\d+)?|\.\d+([eE][+-]?\d+)?|\d+([eE][+-]?\d+)?u?)/)) return 'number'
    if (stream.match(/^(true|false|null)\b/)) return 'atom'
    if (stream.match(/^in\b/)) return 'keyword'
    if (stream.match(/^[A-Za-z_][A-Za-z0-9_]*/)) {
      return stream.peek() === '(' ? 'function' : 'variableName'
    }
    if (stream.match(/^(&&|\|\||==|!=|<=|>=|[-+*/%<>!?:])/)) return 'operator'
    stream.next()
    return 'punctuation'
  },
  tokenTable: { function: t.function(t.variableName) },
  languageData: { commentTokens: { line: '//' } },
})

export const celHighlight = syntaxHighlighting(
  HighlightStyle.define([
    { tag: t.keyword, color: 'var(--syn-kw)', fontWeight: '600' },
    { tag: t.atom, color: 'var(--syn-kw)' },
    { tag: t.string, color: 'var(--syn-str)' },
    { tag: t.number, color: 'var(--syn-num)' },
    { tag: t.function(t.variableName), color: 'var(--syn-fn)' },
    { tag: t.variableName, color: 'var(--codet)' },
    { tag: t.operator, color: 'var(--syn-op)' },
    { tag: t.comment, color: 'var(--muted)', fontStyle: 'italic' },
  ]),
)
